import { useRef } from "react"

import useInfiniteRowVirtualizer from "@hooks/useInfiniteRowVirtualizer"
import useIsAnyDialogOpen from "@hooks/useIsAnyDialogOpen"
import useRouter from "@hooks/useRouter"

import isModKeyPressed from "@utils/isModKeyPressed"

import styles from "./CardList.module.scss"
import { CardListCardProps, CardListProps } from "./CardList.types"

// Initial row-height estimate (card + gap); the virtualizer measures each rendered card and corrects it.
const CARD_HEIGHT = 178

export default function CardList(props: CardListProps) {
    const {
        rows,
        hasNextPage = false,
        isFetchingNextPage = false,
        fetchNextPage,
        getRowRoute,
        onRowHoverChange,
        loaderCard,
        children: renderCard,
    } = props

    const router = useRouter()
    const { isAnyDialogOpen } = useIsAnyDialogOpen()

    const scrollingElement = useRef<HTMLDivElement>(null)

    const { rowVirtualizer, virtualRows } = useInfiniteRowVirtualizer({
        rowCount: rows.length,
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
        getScrollElement: () => scrollingElement.current,
        estimatedRowHeight: CARD_HEIGHT,
        overscan: 5,
    })

    const onCardClick = (detailsURL: string, event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        if (isModKeyPressed(event)) {
            window.open(detailsURL, "_blank", "noopener,noreferrer")
        } else {
            router.push(detailsURL)
        }
    }

    return (
        <div ref={scrollingElement} className={styles.scrollContainer}>
            <div className={styles.virtualizer} style={{ height: `${rowVirtualizer.getTotalSize()}px` }}>
                {virtualRows.map((virtualRow) => {
                    const isLoaderRow = virtualRow.index > rows.length - 1

                    if (isLoaderRow && hasNextPage) {
                        return (
                            <div
                                key={virtualRow.index}
                                className={styles.cardContainer}
                                style={{ transform: `translateY(${virtualRow.start}px)` }}
                                data-index={virtualRow.index}
                                ref={rowVirtualizer.measureElement}
                                aria-busy={true}
                            >
                                {loaderCard}
                            </div>
                        )
                    }

                    const rowData = rows[virtualRow.index]
                    const detailsURL = getRowRoute?.(rowData)
                    const cardId = String(rowData.id)
                    // The card is the interactive element; a row without a route leaves it a plain surface.
                    const cardProps: CardListCardProps = {
                        onClick: detailsURL ? (event) => onCardClick(detailsURL, event) : undefined,
                        isTabbable: !isAnyDialogOpen,
                    }

                    return (
                        <div
                            key={cardId}
                            className={styles.cardContainer}
                            style={{ transform: `translateY(${virtualRow.start}px)` }}
                            id={cardId}
                            data-index={virtualRow.index}
                            ref={rowVirtualizer.measureElement}
                            onMouseEnter={() => onRowHoverChange?.(cardId)}
                        >
                            {renderCard(rowData, cardProps)}
                        </div>
                    )
                })}
            </div>
        </div>
    )
}
